import React, { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from "react";
import { API_DOMAIN } from "config";

const DEFAULT_IMG = "/images/img_car11_qgcqjcnfp_428x738.png";
const LOCAL_PREFIXES = ["/images/", "/assets/", "/static/"];

function isAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:");
}

export default function ImagesCarousel({ images = [] }) {
  const [index, setIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [cardLoaded, setCardLoaded] = useState(false);
  const [rotation, setRotation] = useState(0);

  const displayImages = useMemo(() => {
    const has = Array.isArray(images) && images.length > 0;
    return has ? images : [{ image_url: DEFAULT_IMG, __isDefault: true }];
  }, [images]);

  const hasRealImages = Array.isArray(images) && images.length > 0;
  const canNavigate = displayImages.length > 1;

  const getImgUrl = useCallback((img) => {
    if (!img) return DEFAULT_IMG;
    if (img.__isDefault) return DEFAULT_IMG;

    const url = typeof img.image_url === "string" ? img.image_url.trim() : "";
    if (!url) return DEFAULT_IMG;

    if (isAbsoluteUrl(url)) return url;
    if (LOCAL_PREFIXES.some((p) => url.startsWith(p))) return url;

    const base = String(API_DOMAIN).replace(/\/+$/, "");
    if (url.startsWith("/")) return `${base}${url}`;

    return `${base}/${url}`;
  }, []);

  useEffect(() => {
    setIndex((i) => Math.min(i, displayImages.length - 1));
    setModalIndex((i) => Math.min(i, displayImages.length - 1));
  }, [displayImages.length]);

  useEffect(() => {
    setIsImageLoaded(false);
    setRotation(0);
  }, [modalIndex, isModalOpen]);

  const openModal = (i = 0) => {
    setModalIndex(i);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const rotateImage = (e) => {
    e?.stopPropagation?.();
    setRotation((r) => (r + 90) % 360);
  };

  const prev = () => setIndex((i) => (i === 0 ? displayImages.length - 1 : i - 1));
  const next = () => setIndex((i) => (i === displayImages.length - 1 ? 0 : i + 1));

  const modalPrev = (e) => {
    e?.stopPropagation?.();
    setModalIndex((i) => (i === 0 ? displayImages.length - 1 : i - 1));
  };

  const modalNext = (e) => {
    e?.stopPropagation?.();
    setModalIndex((i) => (i === displayImages.length - 1 ? 0 : i + 1));
  };

  const currentImageUrl = useMemo(() => {
    return getImgUrl(displayImages?.[modalIndex]);
  }, [displayImages, modalIndex, getImgUrl]);

  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const lastActiveElRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen) return;

    lastActiveElRef.current = document.activeElement;
    setTimeout(() => closeBtnRef.current?.focus?.(), 0);

    const handleKey = (e) => {
      if (e.key === "ArrowRight") setModalIndex((i) => (i === displayImages.length - 1 ? 0 : i + 1));
      if (e.key === "ArrowLeft") setModalIndex((i) => (i === 0 ? displayImages.length - 1 : i - 1));
      if (e.key === "Escape") setIsModalOpen(false);
      if (e.key.toLowerCase() === "r") setRotation((r) => (r + 90) % 360);

      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;

        const focusables = root.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const list = Array.from(focusables).filter((el) => !el.hasAttribute("disabled"));
        if (list.length === 0) return;

        const first = list[0];
        const last = list[list.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
      lastActiveElRef.current?.focus?.();
    };
  }, [isModalOpen, displayImages.length]);

  useEffect(() => {
    if (!isModalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow || "";
    };
  }, [isModalOpen]);

  const preloadTimerRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen) return;
    if (!displayImages?.length) return;

    if (preloadTimerRef.current) clearTimeout(preloadTimerRef.current);

    preloadTimerRef.current = setTimeout(() => {
      const preload = (i) => {
        const img = displayImages[i];
        if (!img) return;
        const src = getImgUrl(img);
        const im = new Image();
        im.decoding = "async";
        im.src = src;
      };

      const nextIdx = modalIndex === displayImages.length - 1 ? 0 : modalIndex + 1;
      const prevIdx = modalIndex === 0 ? displayImages.length - 1 : modalIndex - 1;

      preload(nextIdx);
      preload(prevIdx);
    }, 120);

    return () => {
      if (preloadTimerRef.current) clearTimeout(preloadTimerRef.current);
    };
  }, [isModalOpen, modalIndex, displayImages, getImgUrl]);

  const touchRef = useRef({ x: 0, y: 0, t: 0 });

  const onTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    touchRef.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
  };

  const onTouchEnd = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;

    const dx = touch.clientX - touchRef.current.x;
    const dy = touch.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;

    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (dt > 700) return;
    if (absX < 50) return;
    if (absY > 80) return;

    if (dx < 0) modalNext();
    else modalPrev();
  };

  const stop = (e) => e.stopPropagation();

  const cardImageUrl = useMemo(() => getImgUrl(displayImages[index]), [displayImages, index, getImgUrl]);

  useLayoutEffect(() => {
    setCardLoaded(false);
  }, [cardImageUrl]);

  const handleImgError = (e) => {
    if (!e?.currentTarget) return;
    e.currentTarget.src = DEFAULT_IMG;
    setCardLoaded(true);
  };

  return (
    <>
      <div className="ic-card border border-solid border-gray-200 border-b-0">
        {!cardLoaded && <div className="ic-skeleton" aria-hidden="true" />}
        <img
          src={cardImageUrl}
          alt="car"
          className={`ic-slide-img ic-card-fade ${cardLoaded ? "ic-card-fade--show" : "ic-card-fade--hide"}`}
          onClick={() => openModal(index)}
          draggable={false}
          loading={index === 0 ? "eager" : "lazy"}
          decoding="async"
          fetchpriority={index === 0 ? "high" : "auto"}
          onError={handleImgError}
          onLoad={() => setCardLoaded(true)}
        />

        {canNavigate && (
          <>
            <button onClick={prev} type="button" aria-label="הקודם" className="ic-card-arrow ic-card-arrow--left">
              ‹
            </button>
            <button onClick={next} type="button" aria-label="הבא" className="ic-card-arrow ic-card-arrow--right">
              ›
            </button>
          </>
        )}
      </div>

      {isModalOpen && (
        <div className="ic-overlay" role="dialog" aria-modal="true" aria-label="תצוגת תמונה" onClick={closeModal}>
          <div ref={dialogRef} className="ic-dialog" onClick={stop} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="ic-header">
              <div className="ic-header-left">
                <button
                  type="button"
                  aria-label="סובב תמונה"
                  onClick={rotateImage}
                  className="ic-rotate"
                >
                  ↻
                </button>

                <button
                  type="button"
                  aria-label="סגור"
                  ref={closeBtnRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                  className="ic-close"
                >
                  ×
                </button>
              </div>

              <div className="ic-counter">
                {modalIndex + 1} / {displayImages.length}
              </div>

              <div className="ic-header-spacer" aria-hidden="true" />
            </div>

            <div className="ic-center">
              <div className="ic-stage">
                {canNavigate && (
                  <>
                    <button onClick={modalPrev} type="button" aria-label="הקודם" className="ic-lb-arrow ic-lb-arrow--left">
                      ❮
                    </button>
                    <button onClick={modalNext} type="button" aria-label="הבא" className="ic-lb-arrow ic-lb-arrow--right">
                      ❯
                    </button>
                  </>
                )}

                {!isImageLoaded && <div className="ic-loader" />}

                <img
                  src={currentImageUrl}
                  alt={`modal-slide-${modalIndex}`}
                  onLoad={() => setIsImageLoaded(true)}
                  onError={handleImgError}
                  className={`ic-main-img ic-fade ${isImageLoaded ? "ic-fade--show" : "ic-fade--hide"}`}
                  style={{ transform: `rotate(${rotation}deg)` }}
                  draggable={false}
                  decoding="async"
                  fetchpriority="high"
                />

                {hasRealImages && displayImages.length > 1 && (
                  <div className="ic-thumbs-wrap">
                    <div className="ic-thumbs">
                      {displayImages.map((img, i) => {
                        const thumbUrl = getImgUrl(img);
                        const isActive = i === modalIndex;

                        return (
                          <button key={i} type="button" onClick={() => setModalIndex(i)} className="ic-thumb-btn">
                            <div className={`ic-thumb-box ${isActive ? "ic-thumb-box--active" : ""}`}>
                              <img
                                src={thumbUrl}
                                alt={`thumb-${i}`}
                                draggable={false}
                                className="ic-thumb-img"
                                onError={handleImgError}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ic-backdrop-click" onClick={closeModal} />
        </div>
      )}
    </>
  );
}
